import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import {
  Button, Card, Col, Divider, InputNumber, message, Modal,
  Row, Select, Slider, Space, Spin, Steps, Tag,
  Tooltip, Typography, Input, Form, Popconfirm, Badge,
  Radio, List, Table,
} from "antd";
import {
  PlayCircleOutlined, PauseCircleOutlined, StopOutlined,
  ReloadOutlined, PlusOutlined, DeleteOutlined, EditOutlined,
  ClockCircleOutlined, ThunderboltOutlined,
  SaveOutlined, HistoryOutlined, ApartmentOutlined, CopyOutlined,
} from "@ant-design/icons";
import { MdFilterListOff } from "react-icons/md";
import { LuMapPinCheck, LuMapPinX } from "react-icons/lu";
import { IoWarningOutline } from "react-icons/io5";
import { fetchHistoricalData } from "../../LiveMonitoring/Service/liveMonitoring";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { fetchMachineById } from "../../../../configuration/module/MachineConfiguration/Service/machine";
// StompSubscriber replaced by direct STOMP client (subscribeToLiveData)
import ReactApexChart from "react-apexcharts";
import {
  getWriteParameters, getProcedures, createProcedure, updateProcedure,
  deleteProcedure, publishWrite, getWriteLogs,
  startExecution, pauseExecution, resumeExecution, stopExecution, getActiveExecution,
} from "../service/Writecontrol";

const WS_URL = process.env.REACT_APP_API || "https://localhost:8081";

const { Title, Text } = Typography;
const { Option } = Select;

const STEP_TYPES = [
  { value: "SET",                  label: "Set Value",                        color: "#52c41a", icon: <ThunderboltOutlined /> },
  { value: "DURATION",             label: "Wait Duration",                    color: "#1890ff", icon: <ClockCircleOutlined /> },
  { value: "WAIT_CONDITION_SAME",  label: "Wait: Same Param Meets Condition", color: "#fa8c16", icon: <ApartmentOutlined /> },
  { value: "WAIT_CONDITION_OTHER", label: "Wait: Other Param Meets Condition",color: "#722ed1", icon: <ApartmentOutlined /> },
  { value: "WAIT_ANY_CONDITION",   label: "Wait: Any Condition Met",          color: "#eb2f96", icon: <ApartmentOutlined /> },
];

const OPERATORS = [
  { value: "GT",  label: ">" }, { value: "GTE", label: "≥" },
  { value: "LT",  label: "<" }, { value: "LTE", label: "≤" },
  { value: "EQ",  label: "=" }, { value: "NEQ", label: "≠" },
];

const stepTypeMap = Object.fromEntries(STEP_TYPES.map(s => [s.value, s]));

const fmtSeconds = (sec) => {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return `${h}h ${m}m ${s}s`;
};


const WriteControlScreen = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const paramFromUrl = searchParams.get("param");

  // ── Machine data ────────────────────────────────────────────────────────────
  const [machineData, setMachineData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [liveData, setLiveData] = useState({});

  // ── Writable parameters ─────────────────────────────────────────────────────
  const [writeParams, setWriteParams] = useState([]);   // [{parameterKey, displayName, unit, minValue, maxValue}]
  const [selectedParam, setSelectedParam] = useState(null); // will be set from URL in useEffect

  // ── Procedures ──────────────────────────────────────────────────────────────
  const [procedures, setProcedures] = useState([]);
  const [activeProcedure, setActiveProcedure] = useState(null);
  const [editingProcedure, setEditingProcedure] = useState(null); // null = new

  // ── Execution state — driven entirely by backend ──────────────────────────
  const [execState, setExecState]           = useState("idle");
  const [currentStepIdx, setCurrentStepIdx] = useState(0);
  const [startFromStep, setStartFromStep]   = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [executionUuid, setExecutionUuid]   = useState(null);
  const wsClientRef     = useRef(null);  // execution status WebSocket
  const liveWsClientRef = useRef(null);  // live sensor data WebSocket
  const elapsedTimerRef = useRef(null);

  // ── UI / modal state ──────────────────────────────────────────────────────
  const [procedureModalOpen, setProcedureModalOpen] = useState(false);
  const [stepModalOpen, setStepModalOpen]           = useState(false);
  const [editingStep, setEditingStep]               = useState(null);
  const [editingStepIdx, setEditingStepIdx]         = useState(null);
  const [logsVisible, setLogsVisible]               = useState(false);
  const [logs, setLogs]                             = useState([]);
  const [logsLoading, setLogsLoading]               = useState(false);
  const [draftSteps, setDraftSteps]                 = useState([]);
  const [stepForm]  = Form.useForm();
  const [procForm]  = Form.useForm();
  const [chartData, setChartData] = useState({ series: [], timestamps: [] });
  const MAX_CHART_POINTS = 60;


  // ── Execution WebSocket — subscribe to backend status updates ──────────────
  const subscribeToExecution = useCallback((assetUuid) => {
    if (wsClientRef.current) {
      wsClientRef.current.deactivate();
      wsClientRef.current = null;
    }
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_URL}/api/ws`),
      reconnectDelay: 3000,
      onConnect: () => {
        client.subscribe(`/topic/execution/${assetUuid}`, (msg) => {
          const payload = JSON.parse(msg.body);
          if (payload.type !== "EXECUTION_STATUS") return;
          const s = payload.status?.toLowerCase() || "idle";
          setExecState(s);
          setCurrentStepIdx(payload.currentStep ?? 0);
          setElapsedSeconds(payload.elapsedSeconds ?? 0);
          if (payload.executionUuid) setExecutionUuid(payload.executionUuid);
          if (s === "done") message.success("✅ Procedure completed!");
          if (s === "error") message.error("Execution error: " + (payload.message || "Unknown error"));
        });
      },
    });
    client.activate();
    wsClientRef.current = client;
  }, []);

  // ── Live sensor data WebSocket — same as liveMonitoring page ─────────────
  const subscribeToLiveData = useCallback((assetUuid) => {
    if (liveWsClientRef.current) {
      liveWsClientRef.current.deactivate();
      liveWsClientRef.current = null;
    }
    const client = new Client({
      webSocketFactory: () => new SockJS(`${WS_URL}/api/ws`),
      reconnectDelay: 5000,
      debug: (str) => console.log("[LIVE-WS]", str),
      onConnect: () => {
        console.log("[WriteControl] Live data WS connected, topic: /topic/" + assetUuid);
        client.subscribe(`/topic/${assetUuid}`, (message) => {
          try {
            const body = JSON.parse(message.body);
            setLiveData(body);
          } catch (e) {
            console.error("[WriteControl] Failed to parse live message", e);
          }
        });
      },
      onDisconnect: () => console.log("[WriteControl] Live data WS disconnected"),
      onStompError: (frame) => console.error("[WriteControl] Live WS error:", frame.headers["message"]),
    });
    client.activate();
    liveWsClientRef.current = client;
  }, []);

  // Subscribe both WebSockets when uuid is available, clean up on unmount
  useEffect(() => {
    if (uuid) {
      subscribeToExecution(uuid);
      subscribeToLiveData(uuid);
    }
    return () => {
      wsClientRef.current?.deactivate();
      liveWsClientRef.current?.deactivate();
    };
  }, [uuid, subscribeToExecution, subscribeToLiveData]);

  // Local elapsed timer — increments display every second while running
  useEffect(() => {
    if (execState === "running") {
      elapsedTimerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    } else {
      clearInterval(elapsedTimerRef.current);
    }
    return () => clearInterval(elapsedTimerRef.current);
  }, [execState]);



  // ── Backend execution handlers ─────────────────────────────────────────────
  const handleStart = async (fromStep = startFromStep) => {
    if (!activeProcedure) { message.warning("Select a procedure first"); return; }
    if (!activeProcedure.steps?.length) { message.warning("Procedure has no steps"); return; }
    try {
      const res = await startExecution({
        assetUuid:     uuid,
        procedureUuid: activeProcedure.uuid,
        startFromStep: fromStep,
      });
      const execId = res?.data?.data?.executionUuid;
      if (execId) setExecutionUuid(execId);
      setExecState("running");
      setCurrentStepIdx(fromStep);
      setElapsedSeconds(0);
    } catch (err) {
      message.error("Failed to start: " + (err?.response?.data?.message || err.message));
    }
  };

  const handlePause = async () => {
    if (!executionUuid) return;
    try {
      await pauseExecution(executionUuid);
      setExecState("paused");
    } catch (err) {
      message.error("Failed to pause: " + (err?.response?.data?.message || err.message));
    }
  };

  const handleResume = async () => {
    if (!executionUuid) return;
    try {
      await resumeExecution(executionUuid);
      setExecState("running");
    } catch (err) {
      message.error("Failed to resume: " + (err?.response?.data?.message || err.message));
    }
  };

  const handleStop = async () => {
    if (executionUuid) {
      try { await stopExecution(executionUuid); } catch {}
    }
    setExecState("idle");
    setCurrentStepIdx(0);
    setElapsedSeconds(0);
    setExecutionUuid(null);
  };

  const handleReset = () => {
    handleStop();
    setStartFromStep(0);

  };


  // ── Leaf nodes from sampleJson ────────────────────────────────────────────
  const leafNodes = useMemo(() => {
    let parsed = {};
    try {
      parsed = typeof machineData?.sampleJson === "string"
        ? JSON.parse(machineData.sampleJson)
        : machineData?.sampleJson || {};
    } catch {}
    const getLeaves = (obj, prefix = "") => {
      let leaves = [];
      if (typeof obj !== "object" || obj === null) return [];
      for (const [k, v] of Object.entries(obj)) {
        const path = prefix ? `${prefix}.${k}` : k;
        if (typeof v === "object" && v !== null) leaves = leaves.concat(getLeaves(v, path));
        else leaves.push({ key: path, value: v });
      }
      return leaves;
    };
    return getLeaves(parsed).filter(n => n.key !== "timestamp");
  }, [machineData]);

  // ─────────────────────────────────────────────────────────────────────────
  // Load data
  // ─────────────────────────────────────────────────────────────────────────
  const loadMachine = useCallback(async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const res = await fetchMachineById(uuid);
      if (res?.data?.data) setMachineData(res.data.data);
    } catch { message.error("Failed to load asset"); }
    finally { setLoading(false); }
  }, [uuid]);

  const loadWriteParams = useCallback(async () => {
    if (!uuid) return;
    try {
      const res = await getWriteParameters(uuid);
      const data = res?.data?.data || [];
      setWriteParams(data.filter(p => p.enabled));
      if (paramFromUrl) {
        setSelectedParam(paramFromUrl);
      } else if (data.length > 0) {
        setSelectedParam(data[0].parameterKey);
      }
    } catch { message.error("Failed to load writable parameters"); }
  }, [uuid, paramFromUrl]);

  const loadProcedures = useCallback(async () => {
    if (!uuid) return;
    try {
      const res = await getProcedures(uuid);
      const procs = res?.data?.data || [];
      setProcedures(procs);
      // Restore active execution state if procedure is running on backend
      getActiveExecution(uuid)
        .then(execRes => {
          const exec = execRes?.data?.data;
          if (!exec) return;
          const found = procs.find(p => p.uuid === exec.procedureUuid);
          if (found) setActiveProcedure(found);
          setExecState(exec.status?.toLowerCase() || "idle");
          setCurrentStepIdx(exec.currentStepIndex ?? 0);
          setElapsedSeconds(exec.elapsedSeconds ?? 0);
          setExecutionUuid(exec.uuid);
        })
        .catch(() => {});
    } catch {}
  }, [uuid]);

  useEffect(() => {
    loadMachine();
    loadWriteParams();
    loadProcedures();
  }, [loadMachine, loadWriteParams, loadProcedures]);

  // ─────────────────────────────────────────────────────────────────────────
  // Live WebSocket data
  // ─────────────────────────────────────────────────────────────────────────
  const handleLiveMessage = useCallback((body) => {
    setLiveData(body);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────
  // Manual single-write (for ad-hoc values outside a procedure)
  // ─────────────────────────────────────────────────────────────────────────
  const [manualValue, setManualValue] = useState("");
  // targetValue is now derived from backend execution state, not user input
  const handleManualWrite = async () => {
    if (!selectedParam || manualValue === "") {
      message.warning("Select a parameter and enter a value");
      return;
    }
    try {
      await publishWrite({ assetUuid: uuid, parameterKey: selectedParam, value: String(manualValue) });
      message.success(`Written: ${selectedParam} = ${manualValue}`);
    } catch (err) {
      message.error("Write failed: " + (err?.response?.data?.message || err.message));
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Procedure CRUD
  // ─────────────────────────────────────────────────────────────────────────
  const openNewProcedure = () => {
    setEditingProcedure(null);
    setDraftSteps([]);
    procForm.resetFields();
    setProcedureModalOpen(true);
  };

  const openEditProcedure = (proc) => {
    setEditingProcedure(proc);
    setDraftSteps(proc.steps || []);
    procForm.setFieldsValue({ name: proc.name, description: proc.description });
    setProcedureModalOpen(true);
  };

  const handleSaveProcedure = async () => {
    const values = await procForm.validateFields();
    const payload = {
      assetUuid: uuid,
      name: values.name,
      description: values.description,
      steps: draftSteps.map((s, i) => ({ ...s, stepIndex: i })),
    };
    try {
      if (editingProcedure) {
        await updateProcedure(editingProcedure.uuid, payload);
        message.success("Procedure updated");
      } else {
        await createProcedure(payload);
        message.success("Procedure created");
      }
      setProcedureModalOpen(false);
      loadProcedures();
    } catch (err) {
      message.error("Save failed: " + (err?.response?.data?.message || err.message));
    }
  };

  const handleDeleteProcedure = async (procUuid) => {
    await deleteProcedure(procUuid);
    message.success("Deleted");
    loadProcedures();
    if (activeProcedure?.uuid === procUuid) setActiveProcedure(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Step builder (inside procedure modal)
  // ─────────────────────────────────────────────────────────────────────────
  const [stepType, setStepType] = useState("SET");

  const openAddStep = () => {
    setEditingStep(null);
    setEditingStepIdx(null);
    setStepType("SET");
    stepForm.resetFields();
    setStepModalOpen(true);
  };

  const openEditStep = (step, idx) => {
    setEditingStep(step);
    setEditingStepIdx(idx);
    setStepType(step.type);
    stepForm.setFieldsValue(step);
    setStepModalOpen(true);
  };

  const handleSaveStep = async () => {
    const values = await stepForm.validateFields();
    const newStep = { ...values, type: stepType };
    if (editingStepIdx != null) {
      setDraftSteps((prev) => prev.map((s, i) => i === editingStepIdx ? newStep : s));
    } else {
      setDraftSteps((prev) => [...prev, newStep]);
    }
    setStepModalOpen(false);
  };

  const removeStep = (idx) => setDraftSteps((prev) => prev.filter((_, i) => i !== idx));

  const moveStep = (idx, dir) => {
    setDraftSteps((prev) => {
      const arr = [...prev];
      const to = idx + dir;
      if (to < 0 || to >= arr.length) return arr;
      [arr[idx], arr[to]] = [arr[to], arr[idx]];
      return arr;
    });
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Audit logs
  // ─────────────────────────────────────────────────────────────────────────
  const loadLogs = async () => {
    setLogsLoading(true);
    try {
      const res = await getWriteLogs(uuid, { page: 0, size: 50 });
      setLogs(res?.data?.data?.content || []);
    } catch {
      message.error("Failed to load logs");
    } finally {
      setLogsLoading(false);
    }
  };

  const openLogs = () => { loadLogs(); setLogsVisible(true); };

  // ─────────────────────────────────────────────────────────────────────────
  // Derived values
  // ─────────────────────────────────────────────────────────────────────────
  const currentParamMeta = writeParams.find((p) => p.parameterKey === selectedParam) || {};
  const getNestedValue = (obj, path) => path?.split(".").reduce((acc, p) => acc?.[p], obj);
  const _rawLiveValue = selectedParam ? (getNestedValue(liveData, selectedParam) ?? null) : null;
  const currentLiveValue = _rawLiveValue != null && !isNaN(Number(_rawLiveValue)) ? Number(_rawLiveValue) : null;
  const totalSteps = activeProcedure?.steps?.length || 0;
  const currentStep = activeProcedure?.steps?.[currentStepIdx] || null;
  // targetValue from backend execution state (not user input)
  const targetValue = (
    execState !== "idle" &&
    currentStep?.type === "SET" &&
    currentStep?.parameterKey === selectedParam &&
    currentStep?.value != null
  ) ? parseFloat(currentStep.value) : null;

  const progressPct =
    totalSteps > 0 && execState !== "idle"
      ? Math.round((currentStepIdx / totalSteps) * 100)
      : 0;

  const accentColor =
    execState === "running" ? "#52c41a"
    : execState === "paused" ? "#faad14"
    : execState === "done"   ? "#1890ff"
    : execState === "error"  ? "#ff4d4f"
    : "#999";

  // ─────────────────────────────────────────────────────────────────────────
  // Chart config
  // ─────────────────────────────────────────────────────────────────────────
  const chartOptions = {
    chart: { type: "line", toolbar: { show: false }, animations: { enabled: true, speed: 300 } },
    stroke: { curve: "smooth", width: 2 },
    colors: [accentColor, "#faad14"],
    xaxis: {
      type: "datetime",
      categories: chartData.timestamps,
      labels: { show: false },
    },
    yaxis: { labels: { formatter: (v) => v?.toFixed(1) } },
    tooltip: { x: { format: "HH:mm:ss" } },
    legend: { show: true },
    grid: { borderColor: "#f0f0f0" },
  };

  // If we have a setpoint from current step, add it as a reference line
  const chartSeries = [
    { name: currentParamMeta.displayName || selectedParam || "Value", data: chartData.series[0]?.data || [] },
    ...(currentStep?.type === "SET" && currentStep?.parameterKey === selectedParam
      ? [{ name: "Target", data: (chartData.series[0]?.data || []).map(() => parseFloat(currentStep.value) || 0) }]
      : []),
  ];

  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Spin spinning={loading}>
      {/* Live data via dedicated STOMP client — see subscribeToLiveData() */}

      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Write Control — {machineData?.assetName || "Asset"}
          </Title>
          <Text type="secondary">{machineData?.description}</Text>
        </div>
        <Space>
          <Button icon={<HistoryOutlined />} onClick={openLogs}>
            Write Logs
          </Button>
          <Button onClick={() => navigate(-1)}>← Back</Button>
        </Space>
      </div>

      <Row gutter={[16, 16]}>
        {/* ── LEFT: Current status + chart ──────────────────────────────── */}
        <Col xs={24} lg={14}>
          {/* Current temperature card */}
          <Card
            bodyStyle={{ padding: 0, overflow: "hidden" }}
            style={{ marginBottom: 16, borderRadius: 12, overflow: "hidden" }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #e8390e 0%, #f5470a 40%, #ff6a00 100%)",
                padding: "24px 32px 20px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <ThunderboltOutlined style={{ color: "#fff", fontSize: 18 }} />
                <Text style={{ color: "rgba(255,255,255,0.85)", fontWeight: 600 }}>
                  {currentParamMeta.displayName || selectedParam || "Select a parameter"}
                </Text>
              </div>

              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <Title level={1} style={{ margin: 0, color: "#fff", fontSize: 56, lineHeight: 1 }}>
                  {currentLiveValue != null ? Number(currentLiveValue).toFixed(1) : "--"}
                </Title>
                <Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 20, marginBottom: 4 }}>
                  {currentParamMeta.unit || ""}
                </Text>
              </div>

              {/* Progress bar */}
              {currentParamMeta.maxValue != null && currentLiveValue != null && (
                <div style={{ marginTop: 12 }}>
                  <div
                    style={{
                      height: 6, borderRadius: 3,
                      background: "rgba(255,255,255,0.25)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        borderRadius: 3,
                        background: "#fff",
                        width: `${Math.min(100, ((currentLiveValue - (currentParamMeta.minValue || 0)) /
                          ((currentParamMeta.maxValue || 100) - (currentParamMeta.minValue || 0))) * 100)}%`,
                        transition: "width 0.5s ease",
                      }}
                    />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                    <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                      {currentStep?.type === "SET"
                        ? `Target: ${currentStep.value}${currentParamMeta.unit || ""}`
                        : `Min: ${currentParamMeta.minValue ?? "—"}`}
                    </Text>
                    <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 12 }}>
                      {Math.min(100, ((currentLiveValue - (currentParamMeta.minValue || 0)) /
                        ((currentParamMeta.maxValue || 100) - (currentParamMeta.minValue || 0))) * 100).toFixed(0)}%
                    </Text>
                  </div>
                </div>
              )}
            </div>

            {/* Parameter selector row */}
            <div style={{ padding: "12px 32px", background: "#fff", borderTop: "1px solid #f0f0f0" }}>
              <Space wrap>
                {writeParams.map((p) => (
                  <Tag
                    key={p.parameterKey}
                    color={selectedParam === p.parameterKey ? "orange" : "default"}
                    onClick={() => setSelectedParam(p.parameterKey)}
                    style={{ cursor: "pointer", borderRadius: 16, padding: "2px 12px" }}
                  >
                    {p.displayName || p.parameterKey}
                    {getNestedValue(liveData, p.parameterKey) != null && (
                      <span style={{ marginLeft: 4, fontWeight: 600 }}>
                        {!isNaN(Number(getNestedValue(liveData, p.parameterKey)))
                          ? Number(getNestedValue(liveData, p.parameterKey)).toFixed(1)
                          : String(getNestedValue(liveData, p.parameterKey))}
                        {p.unit ? ` ${p.unit}` : ""}
                      </span>
                    )}
                  </Tag>
                ))}
                {writeParams.length === 0 && (
                  <Text type="secondary">No writable parameters configured for this asset.</Text>
                )}
              </Space>
            </div>
          </Card>


          {/* Manual write */}
          <Card title="Manual Write" size="small" style={{ borderRadius: 12 }}>
            <Space wrap>
              <Select
                value={selectedParam}
                onChange={setSelectedParam}
                style={{ width: 200 }}
                placeholder="Select parameter"
              >
                {writeParams.map((p) => (
                  <Option key={p.parameterKey} value={p.parameterKey}>
                    {p.displayName || p.parameterKey}
                  </Option>
                ))}
              </Select>
              <InputNumber
                value={manualValue}
                onChange={(val) => setManualValue(val)}
                placeholder="Value"
                style={{ width: 120 }}
                min={currentParamMeta.minValue}
                max={currentParamMeta.maxValue}
              />
              {currentParamMeta.unit && <Text type="secondary">{currentParamMeta.unit}</Text>}
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                onClick={handleManualWrite}
                disabled={writeParams.length === 0}
              >
                Write Now
              </Button>
            </Space>
            {currentParamMeta.minValue != null && currentParamMeta.maxValue != null && (
              <div style={{ marginTop: 12 }}>
                <Slider
                  min={currentParamMeta.minValue}
                  max={currentParamMeta.maxValue}
                  value={typeof manualValue === "number" ? manualValue : undefined}
                  onChange={(val) => setManualValue(val)}
                  tooltip={{ formatter: (v) => `${v} ${currentParamMeta.unit || ""}` }}
                />
              </div>
            )}
          </Card>
        </Col>

        {/* ── RIGHT: Process status + controls ──────────────────────────── */}
        <Col xs={24} lg={10}>
          {/* Process status */}
          <Card
            size="small"
            style={{ borderRadius: 12, marginBottom: 16 }}
            bodyStyle={{ padding: 16 }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <Badge
                status={
                  execState === "running" ? "processing"
                  : execState === "done"   ? "success"
                  : execState === "error"  ? "error"
                  : execState === "paused" ? "warning"
                  : "default"
                }
              />
              <div>
                <Text type="secondary" style={{ fontSize: 11, textTransform: "uppercase" }}>
                  Process Status
                </Text>
                <br />
                <Text strong style={{ fontSize: 18, color: accentColor }}>
                  {execState.charAt(0).toUpperCase() + execState.slice(1)}
                </Text>
              </div>
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Current Setpoint</Text>
                <div style={{ fontSize: 16, fontWeight: 600 }}>
                  {totalSteps > 0 ? `${currentStepIdx + 1} / ${totalSteps}` : "—"}
                </div>
              </Col>
              <Col span={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Elapsed Time</Text>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{fmtSeconds(elapsedSeconds)}</div>
              </Col>
            </Row>

            <Divider style={{ margin: "12px 0" }} />

            <Text type="secondary" style={{ fontSize: 11 }}>Step Elapsed</Text>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              {fmtSeconds(0)}
            </div>

            {currentStep && (
              <div
                style={{
                  padding: "8px 12px",
                  background: "#f6ffed",
                  borderRadius: 8,
                  border: "1px solid #b7eb8f",
                  fontSize: 12,
                }}
              >
                <Text strong>Current Step:</Text>{" "}
                <Text>{stepTypeMap[currentStep.type]?.label}</Text>
                {currentStep.parameterKey && (
                  <Text> · {currentStep.parameterKey}</Text>
                )}
                {currentStep.value != null && (
                  <Text> = <strong>{currentStep.value}</strong></Text>
                )}
              </div>
            )}
          </Card>

          {/* Controls */}
          <Card title="Controls" size="small" style={{ borderRadius: 12, marginBottom: 16 }}>
            {/* Procedure selector */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Procedure</Text>
              <Select
                value={activeProcedure?.uuid}
                onChange={(val) => {
                  setActiveProcedure(procedures.find((p) => p.uuid === val) || null);
                  handleStop();
                }}
                style={{ width: "100%", marginTop: 4 }}
                placeholder="Select a procedure"
                allowClear
              >
                {procedures.map((p) => (
                  <Option key={p.uuid} value={p.uuid}>{p.name}</Option>
                ))}
              </Select>
            </div>

            {/* Start from step */}
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Start from step (1 = beginning)
              </Text>
              <InputNumber
                min={1}
                max={Math.max(totalSteps, 1)}
                value={startFromStep + 1}
                onChange={(v) => setStartFromStep((v || 1) - 1)}
                style={{ width: "100%", marginTop: 4 }}
                disabled={execState === "running"}
              />
            </div>

            <Space direction="vertical" style={{ width: "100%" }}>
              <Space style={{ width: "100%" }}>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleStart(startFromStep)}
                  disabled={execState === "running" || !activeProcedure}
                  style={{ flex: 1, background: "#52c41a", borderColor: "#52c41a" }}
                  block
                >
                  Start
                </Button>
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={handleStop}
                  disabled={execState === "idle"}
                  block
                >
                  Stop
                </Button>
              </Space>
              <Button
                icon={execState === "paused" ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
                onClick={execState === "paused" ? handleResume : handlePause}
                disabled={execState === "idle" || execState === "done"}
                block
              >
                {execState === "paused" ? "Resume" : "Pause"}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset} block>
                Reset
              </Button>
            </Space>
          </Card>

          {/* Procedure list */}
          <Card
            title="Procedures"
            size="small"
            style={{ borderRadius: 12 }}
            extra={
              <Button size="small" icon={<PlusOutlined />} onClick={openNewProcedure}>
                New
              </Button>
            }
          >
            {procedures.length === 0 && (
              <Text type="secondary">No procedures yet. Create one to automate write sequences.</Text>
            )}
            {procedures.map((proc) => (
              <div
                key={proc.uuid}
                style={{
                  padding: "8px 12px",
                  marginBottom: 6,
                  borderRadius: 8,
                  background: activeProcedure?.uuid === proc.uuid ? "#e6f7ff" : "#fafafa",
                  border: activeProcedure?.uuid === proc.uuid ? "1px solid #91d5ff" : "1px solid #f0f0f0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  cursor: "pointer",
                }}
                onClick={() => { setActiveProcedure(proc); handleStop(); }}
              >
                <div>
                  <Text strong style={{ fontSize: 13 }}>{proc.name}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {proc.steps?.length || 0} steps
                  </Text>
                </div>
                <Space size="small" onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Edit">
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEditProcedure(proc)} />
                  </Tooltip>
                  <Popconfirm
                    title="Delete procedure?"
                    onConfirm={() => handleDeleteProcedure(proc.uuid)}
                    okButtonProps={{ danger: true }}
                  >
                    <Button size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </Card>
        </Col>
      </Row>

      {/* ── Full-width Graph Area ─────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card
            size="small"
            style={{ borderRadius: 12 }}
            bodyStyle={{ padding: 8 }}
          >
            <GraphArea
              data={liveData}
              assetUuid={uuid}
              leafNodes={leafNodes}
              tags={machineData?.tags || []}
              initialSelectedKey={selectedParam || paramFromUrl}
              targetValue={targetValue}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Step list for selected procedure ──────────────────────────────── */}
      {activeProcedure && (
        <Card
          title={`Steps — ${activeProcedure.name}`}
          size="small"
          style={{ marginTop: 16, borderRadius: 12 }}
          extra={
            <Text type="secondary" style={{ fontSize: 12 }}>
              Read-only view. Edit via the procedure builder.
            </Text>
          }
        >
          <Steps
            current={execState !== "idle" ? currentStepIdx : -1}
            direction="vertical"
            size="small"
            style={{ maxHeight: 500, overflowY: "auto" }}
            items={(activeProcedure.steps || []).map((step, idx) => {
              const meta = stepTypeMap[step.type] || {};
              let desc = "";
              if (step.type === "SET")
                desc = `${step.parameterKey} = ${step.value}`;
              else if (step.type === "DURATION")
                desc = fmtSeconds(step.durationSeconds || 0);
              else if (step.type === "WAIT_CONDITION_SAME")
                desc = `${step.parameterKey} ${step.conditionOperator} ${step.conditionValue}`;
              else if (step.type === "WAIT_CONDITION_OTHER")
                desc = `${step.conditionParameterKey} ${step.conditionOperator} ${step.conditionValue}`;
              else if (step.type === "WAIT_ANY_CONDITION")
                desc = `Any of ${(step.conditionParams || []).length} conditions`;

              return {
                title: meta.label || step.type,
                description: desc,
                icon: <span style={{ color: meta.color }}>{meta.icon}</span>,
              };
            })}
          />
        </Card>
      )}

      {/* ── Procedure builder modal ───────────────────────────────────────── */}
      <Modal
        title={editingProcedure ? `Edit: ${editingProcedure.name}` : "New Procedure"}
        open={procedureModalOpen}
        onOk={handleSaveProcedure}
        onCancel={() => setProcedureModalOpen(false)}
        width={780}
        okText={<><SaveOutlined /> Save Procedure</>}
        destroyOnClose
      >
        <Form form={procForm} layout="vertical">
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item name="name" label="Name" rules={[{ required: true }]}>
                <Input placeholder="e.g. Heat Treatment Cycle A" />
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="description" label="Description">
                <Input placeholder="Optional" />
              </Form.Item>
            </Col>
          </Row>
        </Form>

        <Divider orientation="left">Steps</Divider>

        {draftSteps.length === 0 && (
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            No steps yet. Add steps to define the automation sequence.
          </Text>
        )}

        {draftSteps.map((step, idx) => {
          const meta = stepTypeMap[step.type] || {};
          return (
            <div
              key={idx}
              style={{
                padding: "8px 12px",
                marginBottom: 8,
                borderRadius: 8,
                border: "1px solid #f0f0f0",
                background: "#fafafa",
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: meta.color + "20",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: meta.color, fontWeight: 700, fontSize: 13, flexShrink: 0,
                }}
              >
                {idx + 1}
              </div>
              <div style={{ flex: 1 }}>
                <Text strong style={{ color: meta.color }}>{meta.label}</Text>
                {step.type === "SET" && (
                  <Text style={{ marginLeft: 8 }}>{step.parameterKey} ← {step.value}</Text>
                )}
                {step.type === "DURATION" && (
                  <Text style={{ marginLeft: 8 }}>{fmtSeconds(step.durationSeconds || 0)}</Text>
                )}
                {(step.type === "WAIT_CONDITION_SAME" || step.type === "WAIT_CONDITION_OTHER") && (
                  <Text style={{ marginLeft: 8 }}>
                    {step.conditionParameterKey || step.parameterKey}{" "}
                    {OPERATORS.find((o) => o.value === step.conditionOperator)?.label}{" "}
                    {step.conditionValue}
                  </Text>
                )}
                {step.type === "WAIT_ANY_CONDITION" && (
                  <Text style={{ marginLeft: 8 }}>
                    Any of {(step.conditionParams || []).length} conditions
                  </Text>
                )}
                {step.label && (
                  <Text type="secondary" style={{ marginLeft: 8, fontSize: 11 }}>
                    ({step.label})
                  </Text>
                )}
              </div>
              <Space size="small">
                <Button size="small" onClick={() => moveStep(idx, -1)} disabled={idx === 0}>↑</Button>
                <Button size="small" onClick={() => moveStep(idx, 1)} disabled={idx === draftSteps.length - 1}>↓</Button>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEditStep(step, idx)} />
                <Button size="small" icon={<DeleteOutlined />} danger onClick={() => removeStep(idx)} />
              </Space>
            </div>
          );
        })}

        <Button icon={<PlusOutlined />} onClick={openAddStep} style={{ marginTop: 8 }}>
          Add Step
        </Button>
      </Modal>

      {/* ── Step form modal ───────────────────────────────────────────────── */}
      <Modal
        title={editingStep ? "Edit Step" : "Add Step"}
        open={stepModalOpen}
        onOk={handleSaveStep}
        onCancel={() => setStepModalOpen(false)}
        destroyOnClose
      >
        <Form form={stepForm} layout="vertical">
          <Form.Item label="Step Type" required>
            <Select value={stepType} onChange={(v) => { setStepType(v); stepForm.resetFields(["parameterKey","value","durationSeconds","conditionParameterKey","conditionOperator","conditionValue"]); }}>
              {STEP_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>
                  <span style={{ color: t.color }}>{t.icon}</span>{" "}
                  {t.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="label" label="Step Label (optional)">
            <Input placeholder="e.g. Ramp to 500°C" />
          </Form.Item>

          {/* SET */}
          {stepType === "SET" && (
            <>
              <Form.Item name="parameterKey" label="Parameter" rules={[{ required: true }]}>
                <Select placeholder="Select writable parameter">
                  {writeParams.map((p) => (
                    <Option key={p.parameterKey} value={p.parameterKey}>
                      {p.displayName || p.parameterKey} {p.unit ? `(${p.unit})` : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="value" label="Value" rules={[{ required: true }]}>
                <Input placeholder="e.g. 500" />
              </Form.Item>
            </>
          )}

          {/* DURATION */}
          {stepType === "DURATION" && (
            <Form.Item name="durationSeconds" label="Duration (seconds)" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: "100%" }} placeholder="e.g. 3600 = 1 hour" />
            </Form.Item>
          )}

          {/* WAIT_CONDITION_SAME */}
          {stepType === "WAIT_CONDITION_SAME" && (
            <>
              <Form.Item name="parameterKey" label="Parameter to watch" rules={[{ required: true }]}>
                <Select placeholder="Select writable parameter">
                  {writeParams.map((p) => (
                    <Option key={p.parameterKey} value={p.parameterKey}>
                      {p.displayName || p.parameterKey}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="conditionOperator" label="Operator" rules={[{ required: true }]}>
                    <Select>{OPERATORS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}</Select>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name="conditionValue" label="Threshold" rules={[{ required: true }]}>
                    <Input placeholder="e.g. 490" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* WAIT_CONDITION_OTHER */}
          {stepType === "WAIT_CONDITION_OTHER" && (
            <>
              <Form.Item name="conditionParameterKey" label="Other parameter to watch" rules={[{ required: true }]}>
                <Input placeholder="e.g. pressure" list="all-keys" />
                <datalist id="all-keys">
                  {writeParams.map((p) => <option key={p.parameterKey} value={p.parameterKey} />)}
                </datalist>
              </Form.Item>
              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item name="conditionOperator" label="Operator" rules={[{ required: true }]}>
                    <Select>{OPERATORS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}</Select>
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Form.Item name="conditionValue" label="Threshold" rules={[{ required: true }]}>
                    <Input placeholder="e.g. 5" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          {/* WAIT_ANY_CONDITION — simplified UI, one condition entry */}
          {stepType === "WAIT_ANY_CONDITION" && (
            <div style={{ padding: 12, background: "#f9f0ff", borderRadius: 8, border: "1px solid #d3adf7" }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Define multiple conditions — execution advances when ANY ONE is met.
                (Use the WAIT_CONDITION_SAME or OTHER types for single conditions.)
              </Text>
              <Form.Item name="conditionParams" label="Conditions (JSON)" style={{ marginTop: 8 }}>
                <Input.TextArea
                  rows={4}
                  placeholder={`[\n  {"parameterKey":"temperature","operator":"GTE","conditionValue":"490"},\n  {"parameterKey":"pressure","operator":"LT","conditionValue":"5"}\n]`}
                />
              </Form.Item>
            </div>
          )}

          <Form.Item name="timeoutSeconds" label="Timeout (seconds, optional)">
            <InputNumber min={0} style={{ width: "100%" }} placeholder="Leave blank for no timeout" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Audit logs drawer ─────────────────────────────────────────────── */}
      <Modal
        title="Write Audit Logs"
        open={logsVisible}
        onCancel={() => setLogsVisible(false)}
        footer={null}
        width={900}
      >
        <Table
          loading={logsLoading}
          dataSource={logs}
          rowKey="uuid"
          size="small"
          pagination={{ pageSize: 20 }}
          columns={[
            { title: "Time", dataIndex: "triggeredAt", key: "triggeredAt",
              render: (v) => v ? new Date(v).toLocaleString() : "—", width: 160 },
            { title: "By",   dataIndex: "triggeredBy",  key: "triggeredBy", width: 140 },
            { title: "Procedure", dataIndex: "procedureName", key: "procedureName", width: 150,
              render: (v) => v || <Text type="secondary">Manual</Text> },
            { title: "Step", dataIndex: "stepIndex", key: "stepIndex", width: 60,
              render: (v) => v != null ? v + 1 : "—" },
            { title: "Parameter", dataIndex: "parameterKey", key: "parameterKey", width: 120,
              render: (v) => <Text code>{v}</Text> },
            { title: "Value", dataIndex: "valueSent", key: "valueSent", width: 80 },
            { title: "Status", dataIndex: "success", key: "success", width: 80,
              render: (v, r) => v
                ? <Tag color="success">OK</Tag>
                : <Tooltip title={r.errorMessage}><Tag color="error">FAIL</Tag></Tooltip> },
          ]}
        />
      </Modal>
    </Spin>
  );
};


// ── GraphArea helpers (from liveMonitoring) ──────────────────────────────────
const ONE_MIN_PAGE_MS = 1 * 60 * 1000;

const formatWindowLabel = (aggType, start, end) => {
  if (aggType === "LIVE") return "Live Data (Current)";
  if (!start || !end) return "";

  const s = new Date(start);
  const e = new Date(end);

  switch (aggType) {
    case "YEAR":
      return `${s.toLocaleString("default", {
        month: "short",
        year: "numeric",
      })} → ${e.toLocaleString("default", {
        month: "short",
        year: "numeric",
      })}`;

    case "MONTH":
      return `${s.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} → ${e.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`;

    case "DAY":
    case "HOUR":
    // case "QUARTER":
    case "RAW":
      return `${s.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })} → ${e.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })}`;

    // case "LIVE":
    //   return "Live Data (Current)";
    case "LIVE": {
      const now = new Date();

      const dateStr = now.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });

      const timeStr = now.toLocaleTimeString("en-IN", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      // ✅ Extract current live value
      let liveValue = "-";
      if (start && typeof start === "object" && start.liveValue !== undefined) {
        liveValue = start.liveValue;
      }

      return `Live Data (Current) — ${dateStr} ${timeStr} — Value: ${liveValue}`;
    }

    default:
      return "";
  }
};

const toISOStringTrimmed = (d) => new Date(d).toISOString();

// const startOfToday = (d = new Date()) => new Date(d.setHours(0, 0, 0, 0));
const startOfToday = (d = new Date()) => {
  const copy = new Date(d); // make a copy
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const startOfMonth = (d = new Date()) =>
  new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const startOfYear = (d = new Date()) =>
  new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
const endOfMonth = (d) => {
  const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  e.setHours(23, 59, 59, 999);
  return e;
};
const endOfYear = (d) => {
  const e = new Date(d.getFullYear(), 11, 31);
  e.setHours(23, 59, 59, 999);
  return e;
};


const GraphArea = ({ data, leafNodes, tags = [], assetUuid, navigate, targetValue, initialSelectedKey }) => {
  const [writableKeys, setWritableKeys] = React.useState(new Set());

  React.useEffect(() => {
    if (!assetUuid) return;
    getWriteParameters(assetUuid)
      .then(res => {
        const keys = (res?.data?.data || [])
          .filter(p => p.enabled)
          .map(p => p.parameterKey);
        setWritableKeys(new Set(keys));
      })
      .catch(() => {});
  }, [assetUuid]);

  const [selectedKey, setSelectedKey] = useState(initialSelectedKey || null);

  // Sync when initialSelectedKey changes
  useEffect(() => {
    if (initialSelectedKey && selectedKey !== initialSelectedKey) {
      setSelectedKey(initialSelectedKey);
      setLiveSeries([]);
    }
  }, [initialSelectedKey]);
  const [filterType, setFilterType] = useState("All");
  const [aggregationType, setAggregationType] = useState("LIVE");
  const [chartData, setChartData] = useState([]);
  const [pageWindow, setPageWindow] = useState({ start: null, end: null });
  const [isLoading, setIsLoading] = useState(false);
  const rawInitialRef = useRef({ start: null, end: null });
  const [liveSeries, setLiveSeries] = useState([]);
  const [selectorOptions, setSelectorOptions] = useState([]); // { value, label, disabled }
  const [selectorValue, setSelectorValue] = useState(null);

  const generateTimeSlots = (aggType, start, end) => {
    const slots = [];
    const s = new Date(start);
    const e = new Date(end);

    // ✅ Normalize start to clean boundary
    if (aggType === "DAY") s.setHours(0, 0, 0, 0);
    if (aggType === "HOUR") {
      // normalize to nearest lower 15-min mark
      const minutes = s.getMinutes();
      s.setMinutes(Math.floor(minutes / 15) * 15, 0, 0);
    }
    if (aggType === "MONTH") s.setHours(0, 0, 0, 0);
    if (aggType === "YEAR") s.setMonth(0, 1, 0, 0, 0, 0, 0);

    switch (aggType) {
      case "YEAR":
        for (let i = 0; i < 12; i++) {
          slots.push({ x: new Date(s.getFullYear(), i, 1), y: null });
        }
        break;

      case "MONTH": {
        const daysInMonth = new Date(
          s.getFullYear(),
          s.getMonth() + 1,
          0
        ).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          slots.push({
            x: new Date(s.getFullYear(), s.getMonth(), i),
            y: null,
          });
        }
        break;
      }

      case "DAY":
        for (let i = 0; i < 24; i++) {
          const label = new Date(s);
          label.setHours(i, 0, 0, 0);
          slots.push({ x: label, y: null });
        }
        break;

      case "HOUR":
        // ✅ Always generate from normalized start, every 15 minutes
        for (let i = 0; i < 60; i += 15) {
          const label = new Date(s);
          label.setMinutes(s.getMinutes() + i, 0, 0);
          slots.push({ x: label, y: null });
        }
        break;

      case "RAW":
        for (let i = 0; i < 60; i += 5) {
          const label = new Date(s);
          label.setSeconds(i, 0);
          slots.push({ x: label, y: null });
        }
        break;

      default:
        slots.push({ x: new Date(), y: null });
    }

    return slots;
  };

  // helpers: gets real timestamps from aggregated chartData items
  const extractTimestampsFromAggData = (apiData, selectedKey) => {
    if (!apiData || !apiData.length) return [];
    const tsList = [];
    for (const itm of apiData) {
      // aggregated responses in your code store timestamps under highData / lowData
      const h = itm.highData?.[selectedKey]?.timestamp;
      const l = itm.lowData?.[selectedKey]?.timestamp;
      const ts = h ?? l ?? itm.timestamp ?? itm.TimeStamp;
      if (ts) {
        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);
        tsList.push(date);
      }
    }
    return tsList;
  };

  const buildYearOptions = (dates) => {
    // input: array of Date objects
    const presentMonths = new Set(dates.map((d) => d.getMonth()));
    const opts = Array.from({ length: 12 }, (_, i) => {
      const label = new Date(0, i, 1).toLocaleString("en-IN", {
        month: "short",
      });
      return { value: i, label, disabled: !presentMonths.has(i) };
    });
    return opts;
  };

  const buildMonthOptions = (dates, referenceDate) => {
    // show all days in that month; enable if any timestamp exists on that day
    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const presentDays = new Set(dates.map((d) => d.getDate()));
    const opts = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return {
        value: day,
        label: String(day).padStart(2, "0"),
        disabled: !presentDays.has(day),
      };
    });
    return opts;
  };

  const buildDayOptions = (dates) => {
    // hours 0..23, enable if any timestamp in that hour
    const presentHours = new Set(dates.map((d) => d.getHours()));
    const opts = Array.from({ length: 24 }, (_, i) => {
      const label = `${String(i).padStart(2, "0")}:00–${String(
        (i + 1) % 24
      ).padStart(2, "0")}:00`;
      return { value: i, label, disabled: !presentHours.has(i) };
    });
    return opts;
  };

  const buildHourOptions = (dates, hourStartDate) => {
    // 4 ranges: 00-14,15-29,30-44,45-59 for that hour
    const ranges = [
      { startMin: 0, endMin: 14, label: "00–14" },
      { startMin: 15, endMin: 29, label: "15–29" },
      { startMin: 30, endMin: 44, label: "30–44" },
      { startMin: 45, endMin: 59, label: "45–59" },
    ];
    const opts = ranges.map((r, idx) => {
      const has = dates.some((d) => {
        // ensure same hour and minute in range
        return (
          d.getHours() === hourStartDate.getHours() &&
          d.getFullYear() === hourStartDate.getFullYear() &&
          d.getMonth() === hourStartDate.getMonth() &&
          d.getDate() === hourStartDate.getDate() &&
          d.getMinutes() >= r.startMin &&
          d.getMinutes() <= r.endMin
        );
      });
      return {
        value: idx, // 0..3
        label: r.label,
        disabled: !has,
        meta: r, // keep for later computation
      };
    });
    return opts;
  };

  const transformChartData = (apiData, selectedKey, tag, aggType) => {
    if (!selectedKey) return [];

    // ✅ RAW MODE — use direct timestamps and parameter values (no slots, no high/low)
    if (aggType === "RAW") {
      if (!apiData || !apiData.length) return [];

      const rawSeries = apiData.map((item) => {
        const ts =
          item.timestamp || item.TimeStamp || item.parameters?.TimeStamp;

        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);

        const val = item.parameters?.[selectedKey];

        return {
          x: date,
          y: val !== undefined ? Number(val) : null,
        };
      });

      // ✅ Base RAW graph line
      const series = [
        {
          name: `${selectedKey} (Raw)`,
          data: rawSeries,
          type: "line",
          color: "#007bff",
        },
      ];

      // ✅ Add condition lines for RAW mode
      if (tag?.conditions?.length) {
        tag.conditions.forEach((c) => {
          series.push({
            name: `Condition ${c.operator} ${c.value}`,
            data: rawSeries.map((d) => ({ x: d.x, y: Number(c.value) })),
            type: "line",
            color: "#ff4757",
            dashArray: 5,
          });
        });
      }

      return series;
    }

    const baseSlots = generateTimeSlots(
      aggType,
      pageWindow.start,
      pageWindow.end
    );

    const filledHigh = baseSlots.map((slot) => {
      const matchedItem = apiData?.find((itm) => {
        const ts =
          itm.highData?.[selectedKey]?.timestamp ||
          itm.lowData?.[selectedKey]?.timestamp;
        if (!ts) return false;

        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);

        switch (aggType) {
          case "YEAR":
            return date.getMonth() === slot.x.getMonth();
          case "MONTH":
            return date.getDate() === slot.x.getDate();
          case "DAY":
            return date.getHours() === slot.x.getHours();
          case "HOUR":
            // return date.getMinutes() === slot.x.getMinutes();
            const slotStart = slot.x;
            const slotEnd = new Date(slot.x.getTime() + 15 * 60 * 1000);
            return date >= slotStart && date < slotEnd;

          default:
            return false;
        }
      });

      return {
        x: slot.x,
        y: matchedItem
          ? matchedItem.highData?.[selectedKey]?.value ?? null
          : null,
      };
    });

    const filledLow = baseSlots.map((slot) => {
      const matchedItem = apiData?.find((itm) => {
        const ts =
          itm.highData?.[selectedKey]?.timestamp ||
          itm.lowData?.[selectedKey]?.timestamp;
        if (!ts) return false;

        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);

        switch (aggType) {
          case "YEAR":
            return date.getMonth() === slot.x.getMonth();
          case "MONTH":
            return date.getDate() === slot.x.getDate();
          case "DAY":
            return date.getHours() === slot.x.getHours();
          case "HOUR": {
            const slotStart = slot.x;
            const slotEnd = new Date(slot.x.getTime() + 15 * 60 * 1000);
            return date >= slotStart && date < slotEnd;
          }
          default:
            return false;
        }
      });

      return {
        x: slot.x,
        y: matchedItem
          ? matchedItem.lowData?.[selectedKey]?.value ?? null
          : null,
      };
    });

    const series = [
      {
        name: `${selectedKey} (High)`,
        data: filledHigh,
        type: "line",
        color: "#007bff",
      },
      {
        name: `${selectedKey} (Low)`,
        data: filledLow,
        type: "line",
        color: "#00b894",
      },
    ];

    if (tag?.conditions?.length) {
      tag.conditions.forEach((c) => {
        series.push({
          name: `Condition ${c.operator} ${c.value}`,
          data: baseSlots.map((d) => ({ x: d.x, y: Number(c.value) })),
          type: "line",
          color: "#ff4757",
          dashArray: 5,
        });
      });
    }

    return series;
  };

  const getChartOptions = (aggType) => {
    let xFormat;
    switch (aggType) {
      case "YEAR":
        xFormat = "MMM"; // Jan, Feb, Mar...
        break;
      case "MONTH":
        xFormat = "dd MMM"; // 01 Oct
        break;
      case "DAY":
        xFormat = "HH:mm"; // 10:30
        break;
      case "HOUR":
        xFormat = "mm"; // minute
        break;
      case "RAW":
        xFormat = "ss"; // seconds
        break;
      case "LIVE":
        xFormat = "HH:mm:ss";
        break;

      default:
        xFormat = "dd MMM HH:mm";
    }

    return {
      chart: {
        id: "rangeChart",
        type: "line",
        zoom: { enabled: false },
        toolbar: { show: true },
        animations: { enabled: true },

        events: {},
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          formatter: (val, index, opts) => {
            const date = new Date(val);

            const pad = (n) => n.toString().padStart(2, "0");

            switch (aggType) {
              case "YEAR":
                return date.toLocaleString("en-IN", { month: "short" }); // Jan, Feb
              case "MONTH":
                return pad(date.getDate()); // 01, 02, 03...
              case "DAY": {
                const h = pad(date.getHours());
                const next = pad((date.getHours() + 1) % 24);
                return `${h}:00–${next}:00`;
              }
              case "HOUR": {
                const m = pad(date.getMinutes());
                const next = pad((date.getMinutes() + 15) % 60);
                return `${m}–${next} min`;
              }
              case "RAW": {
                const s = pad(date.getSeconds());
                const next = pad((date.getSeconds() + 5) % 60);
                // return `${s}–${next}`;
                return `${s} sec`;
              }
              case "LIVE":
                return date.toLocaleTimeString("en-IN", {
                  hour12: false,
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                });
              default:
                return date.toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                });
            }
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      markers: {
        size: 3,
        hover: { size: 5 },
        strokeWidth: 2, // keeps visibility strong
        strokeOpacity: 1,
        fillOpacity: 1,
        discrete: [],
      },
      tooltip: {
        x: { format: "dd MMM yyyy, HH:mm" },
      },
    };
  };

  const getValueFromPath = (obj, path) =>
    path?.split(".").reduce((acc, p) => acc?.[p], obj);

  const isConditionMet = (value, condition) => {
    const val = isNaN(value) ? value : Number(value);
    const condVal = isNaN(condition.value)
      ? condition.value
      : Number(condition.value);
    switch (condition.operator) {
      case "==":
        return val === condVal;
      case "!=":
        return val !== condVal;
      case ">":
        return val > condVal;
      case "<":
        return val < condVal;
      case ">=":
        return val >= condVal;
      case "<=":
        return val <= condVal;
      default:
        return false;
    }
  };

  const mappedKeySet = useMemo(
    () => new Set(tags.filter((t) => t?.key).map((t) => t.key)),
    [tags]
  );

  const alertKeySet = useMemo(() => {
    const s = new Set();
    for (const tag of tags) {
      const val = getValueFromPath(data, tag.key);
      if (tag.conditions?.some((c) => isConditionMet(val, c))) s.add(tag.key);
    }
    return s;
  }, [tags, data]);

  const filteredLeafNodes = useMemo(() => {
    if (!leafNodes) return [];
    switch (filterType) {
      case "Mapped":
        return leafNodes.filter((n) => mappedKeySet.has(n.key));
      case "Unmapped":
        return leafNodes.filter((n) => !mappedKeySet.has(n.key));
      case "Alert":
        return leafNodes.filter((n) => alertKeySet.has(n.key));
      default:
        return leafNodes;
    }
  }, [leafNodes, filterType, mappedKeySet, alertKeySet]);

  const parameterOptions = [
    {
      label: (
        <Tooltip title="All parameters">
          <MdFilterListOff />
        </Tooltip>
      ),
      value: "All",
    },
    {
      label: (
        <Tooltip title="Mapped parameters">
          <LuMapPinCheck />
        </Tooltip>
      ),
      value: "Mapped",
    },
    {
      label: (
        <Tooltip title="Unmapped parameters">
          <LuMapPinX />
        </Tooltip>
      ),
      value: "Unmapped",
    },
    {
      label: (
        <Tooltip title="Alert parameters">
          <IoWarningOutline />
        </Tooltip>
      ),
      value: "Alert",
    },
  ];

  /* ---------- Window Computation ---------- */

  const computeInitialWindow = (aggType, now = new Date()) => {
    switch (aggType) {
      case "RAW": {
        const start = new Date(now);
        start.setSeconds(0, 0);
        const end = new Date(start.getTime() + ONE_MIN_PAGE_MS);
        return { start, end };
      }
      case "HOUR": {
        const rounded = new Date(now);
        rounded.setMinutes(0, 0, 0);
        const start = new Date(rounded);
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        return { start, end };
      }
      case "DAY": {
        const start = startOfToday(now);
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999
        );
        return { start, end };
      }

      case "MONTH":
        return { start: startOfMonth(now), end: endOfMonth(now) };

      case "YEAR":
        return { start: startOfYear(now), end: endOfYear(now) };

      default:
        return { start: new Date(now - ONE_MIN_PAGE_MS), end: now };
    }
  };

  const shiftWindow = (aggType, current, dir) => {
    if (!current.start || !current.end) return null;
    const { start, end } = current;

    const add = (unitMs) => {
      return dir === "prev"
        ? { start: new Date(start - unitMs), end: new Date(end - unitMs) }
        : {
            start: new Date(start.getTime() + unitMs),
            end: new Date(end.getTime() + unitMs),
          };
    };

    switch (aggType) {
      case "YEAR": {
        const currentYear = start.getFullYear();
        const targetYear = dir === "prev" ? currentYear - 1 : currentYear + 1;

        return {
          start: new Date(targetYear, 0, 1, 0, 0, 0, 0),
          end: new Date(targetYear, 11, 31, 23, 59, 59, 999),
        };
      }
      case "MONTH":
        const base = new Date(start);
        const next =
          dir === "prev"
            ? new Date(base.getFullYear(), base.getMonth() - 1, 1)
            : new Date(base.getFullYear(), base.getMonth() + 1, 1);
        return { start: startOfMonth(next), end: endOfMonth(next) };
      case "DAY": {
        const cur = new Date(start);
        const target =
          dir === "prev"
            ? new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() - 1)
            : new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);

        return {
          start: new Date(
            target.getFullYear(),
            target.getMonth(),
            target.getDate(),
            0,
            0,
            0,
            0
          ),
          end: new Date(
            target.getFullYear(),
            target.getMonth(),
            target.getDate(),
            23,
            59,
            59,
            999
          ),
        };
      }

      case "HOUR":
        return add(60 * 60 * 1000);
      // case "QUARTER":
      //   return add(15 * 60 * 1000);
      case "RAW":
        return add(60 * 1000);
      default:
        return null;
    }
  };

  /* ---------- API ---------- */
  const fetchHistorical = async ({ assetUuid, aggType, start, end }) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        assetUuid,
        aggregationType: aggType,
        start: toISOStringTrimmed(start),
        end: toISOStringTrimmed(end),
      });
      const res = await fetchHistoricalData(params);
      console.log("res.data", res.data);
      // if (aggType === "RAW") {
      //   setChartData(res.data.content || []);
      // } else {
      //   setChartData(res.data || []);
      // }
      setChartData(res.data || []);
      setPageWindow({ start, end });

      if (selectedKey) {
        const apiData =
          aggType === "RAW" ? res.data.content || [] : res.data || [];
      }

      // setChartData(res.data);
      setPageWindow({ start, end });
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialWindow = async (aggType, assetUuid) => {
    setAggregationType(aggType);
    const win = computeInitialWindow(aggType);

    setPageWindow({ start: win.start, end: win.end });

    if (aggType === "HOUR")
      rawInitialRef.current = {
        start: win.start.toISOString(),
        end: win.end.toISOString(),
      };

    await fetchHistorical({
      assetUuid,
      aggType,
      start: win.start,
      end: win.end,
    });
  };

  const onPrevious = async () => {
    const prev = shiftWindow(aggregationType, pageWindow, "prev");
    if (prev) {
      await fetchHistorical({
        assetUuid,
        aggType: aggregationType,
        start: prev.start,
        end: prev.end,
      });
      setPageWindow(prev); // ✅ update the window after successful call
    }
  };

  const onNext = async () => {
    const next = shiftWindow(aggregationType, pageWindow, "next");
    if (next) {
      await fetchHistorical({
        assetUuid,
        aggType: aggregationType,
        start: next.start,
        end: next.end,
      });
      setPageWindow(next); // ✅ update here too
    }
  };

  const historicalChartCall = (aggType) => {
    if (aggType === "LIVE") {
      setAggregationType("LIVE");
      setChartData([]);
      setPageWindow({ start: null, end: null });

      // ✅ CLEAR PREVIOUS LIVE POINTS WHEN ENTERING GL MODE
      setLiveSeries([]);
      return;
    }

    loadInitialWindow(aggType, assetUuid);
  };

  // Sort the filtered list alphabetically by displayName, then key
  const sortedLeafNodes = useMemo(() => {
    return [...filteredLeafNodes].sort((a, b) => {
      const tagA = tags.find((t) => t.key === a.key);
      const tagB = tags.find((t) => t.key === b.key);

      const nameA = tagA?.displayName?.trim() || null;
      const nameB = tagB?.displayName?.trim() || null;

      // If both have display names → sort alphabetically by displayName
      if (nameA && nameB) {
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      }

      // If only one has displayName → that one comes first
      if (nameA && !nameB) return -1;
      if (!nameA && nameB) return 1;

      // If neither has displayName → sort alphabetically by key
      return a.key.localeCompare(b.key, undefined, { sensitivity: "base" });
    });
  }, [filteredLeafNodes, tags]);

  useEffect(() => {
    if (aggregationType !== "LIVE") return;
    if (!selectedKey) return;

    const raw = getValueFromPath(data, selectedKey);
    const num = Number(raw);
    // Skip if value is missing or not a valid number
    if (raw == null || isNaN(num)) return;

    const point = { x: new Date(), y: num };

    setLiveSeries((prev) => {
      const updated = [...prev, point];
      // Keep latest 60 points for a smooth live graph
      if (updated.length > 60) return updated.slice(updated.length - 60);
      return updated;
    });
  }, [data, selectedKey, aggregationType]);

  useEffect(() => {
    // build selector options from chartData for currently selectedKey + aggregation
    const key = selectedKey;
    if (!key) {
      setSelectorOptions([]);
      setSelectorValue(null);
      return;
    }

    // extract timestamps from chartData using selectedKey
    const dates = extractTimestampsFromAggData(chartData, key);

    if (aggregationType === "YEAR") {
      const opts = buildYearOptions(dates);
      setSelectorOptions(opts.map((o) => ({ ...o, label: `${o.label}` })));
      setSelectorValue(null);
    } else if (aggregationType === "MONTH") {
      // need reference month: use pageWindow.start (which should be the month start)
      const ref = pageWindow.start ? new Date(pageWindow.start) : new Date();
      const opts = buildMonthOptions(dates, ref);
      setSelectorOptions(opts);
      setSelectorValue(null);
    } else if (aggregationType === "DAY") {
      const opts = buildDayOptions(dates);
      setSelectorOptions(opts);
      setSelectorValue(null);
    } else if (aggregationType === "HOUR") {
      // for HOUR, pageWindow.start is the hour start
      const ref = pageWindow.start ? new Date(pageWindow.start) : new Date();
      const opts = buildHourOptions(dates, ref);
      setSelectorOptions(opts);
      setSelectorValue(null);
    } else {
      setSelectorOptions([]);
      setSelectorValue(null);
    }
  }, [chartData, aggregationType, selectedKey, pageWindow.start]);

  /* ---------- Render ---------- */
  return (
    <div>
      <h2 style={{ paddingBottom: 16 }}>Parameter Graphs</h2>
      <Row gutter={16}>
        {/* LEFT PANEL */}
        <Col span={6}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            block
            options={parameterOptions}
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setSelectedKey(null);
            }}
            style={{ marginBottom: 16 }}
          />
          <List
            bordered
            // dataSource={filteredLeafNodes}
            dataSource={sortedLeafNodes.filter(
              (item) => item.key !== "timestamp"
            )}
            style={{ height: 500, overflowY: "auto" }}
            renderItem={(item) => {
              const isAlerting = alertKeySet.has(item.key);
              const isSelected = item.key === selectedKey;
              const tag = tags.find((t) => t.key === item.key);
              const val = getValueFromPath(data, item.key) ?? "-";
              return (
                <List.Item
                  // onClick={() => setSelectedKey(item.key)}
                  onClick={() => {
                    // 1️⃣ Clear old data
                    setLiveSeries([]);
                    setChartData([]);
                    setPageWindow({ start: null, end: null });
                    setSelectorOptions([]);
                    setSelectorValue(null);

                    // 2️⃣ Switch parameter
                    setSelectedKey(item.key);

                    // 3️⃣ If not LIVE, reload historical
                    if (aggregationType !== "LIVE") {
                      historicalChartCall(aggregationType);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? isAlerting
                        ? "rgba(255,50,50,0.2)"
                        : "#e6f7ff"
                      : "transparent",
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                  }}
                >
                  {/* LEFT SIDE: Label + Key (Two Lines) */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600 }}>
                      {tag?.displayName || "-"}
                    </span>
                    <span style={{ fontSize: 12, color: "#777" }}>
                      {item.key}
                    </span>
                  </div>

                  {/* RIGHT SIDE: Value */}
                  <div style={{ fontWeight: 500 }}>{String(val)}</div>
                </List.Item>
              );
            }}
          />
        </Col>

        {/* RIGHT PANEL */}
        <Col span={18}>
          <Card
            // title={selectedKey ? `Tag: ${selectedKey}` : "Select a parameter"}
            title={
              selectedKey && (
                <div style={{ padding: "1rem" }}>
                  <h3>
                    Tag:{" "}
                    {tags.find((t) => t.key === selectedKey)?.displayName ||
                      selectedKey}
                  </h3>
                </div>
              )
            }
            extra={
              <Space>
                <Radio.Group
                  defaultValue="LIVE"
                  optionType="button"
                  value={aggregationType}
                  buttonStyle="solid"
                  disabled={selectedKey ? false : true}
                  options={[
                    { label: "1Y", value: "YEAR" },
                    { label: "1M", value: "MONTH" },
                    { label: "1D", value: "DAY" },
                    { label: "1H", value: "HOUR" },
                    { label: "1Min", value: "RAW" },
                    {
                      label: <Tooltip title="Go Live">GL</Tooltip>,
                      value: "LIVE",
                    },
                  ]}
                  onChange={(e) => historicalChartCall(e.target.value)}
                />
                {selectedKey && navigate && writableKeys.has(selectedKey) && (
                  <Tooltip title="Open Write Control for this parameter">
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      style={{ background: "#fa8c16", borderColor: "#fa8c16" }}
                      onClick={() =>
                        navigate(
                          `/ams/write-control/${assetUuid}?param=${encodeURIComponent(selectedKey)}`
                        )
                      }
                    >
                      Write Control
                    </Button>
                  </Tooltip>
                )}
              </Space>
            }
            style={{ minHeight: 350 }}
          >
            {selectedKey ? (
              <>
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 10,
                    fontWeight: 500,
                  }}
                >
                  {aggregationType === "LIVE"
                    ? `${formatWindowLabel(
                        "LIVE",
                        { liveValue: getValueFromPath(data, selectedKey) }, // ✅ inject value
                        null
                      )} = ${getValueFromPath(data, selectedKey)}`
                    : formatWindowLabel(
                        aggregationType,
                        pageWindow.start,
                        pageWindow.end
                      )}
                  {/* Put this right under the formatted window label */}
                  {selectedKey && selectorOptions.length > 0 && (
                    <div style={{ textAlign: "center", marginBottom: 10 }}>
                      <Select
                        value={selectorValue}
                        onChange={async (val) => {
                          setSelectorValue(val);
                          const opt = selectorOptions.find(
                            (o) => o.value === val
                          );

                          let nextAgg = aggregationType;
                          let newStart = null;
                          let newEnd = null;

                          // --- 1️⃣ YEAR → MONTH ---
                          if (aggregationType === "YEAR") {
                            const year = pageWindow.start
                              ? new Date(pageWindow.start).getFullYear()
                              : new Date().getFullYear();
                            const m = Number(val);
                            newStart = new Date(year, m, 1, 0, 0, 0, 0);
                            newEnd = endOfMonth(newStart);
                            nextAgg = "MONTH";
                          }

                          // --- 2️⃣ MONTH → DAY ---
                          else if (aggregationType === "MONTH") {
                            const base = pageWindow.start
                              ? new Date(pageWindow.start)
                              : new Date();
                            const year = base.getFullYear();
                            const month = base.getMonth();
                            const day = Number(val);
                            newStart = new Date(year, month, day, 0, 0, 0, 0);
                            newEnd = new Date(
                              year,
                              month,
                              day,
                              23,
                              59,
                              59,
                              999
                            );
                            nextAgg = "DAY";
                          }

                          // --- 3️⃣ DAY → HOUR ---
                          else if (aggregationType === "DAY") {
                            const base = pageWindow.start
                              ? new Date(pageWindow.start)
                              : new Date();
                            const year = base.getFullYear();
                            const month = base.getMonth();
                            const day = base.getDate();
                            const hour = Number(val);
                            newStart = new Date(
                              year,
                              month,
                              day,
                              hour,
                              0,
                              0,
                              0
                            );
                            newEnd = new Date(
                              year,
                              month,
                              day,
                              hour + 1,
                              0,
                              0,
                              0
                            );
                            nextAgg = "HOUR";
                          }

                          // --- 4️⃣ HOUR → RAW (15-min slot) ---
                          else if (aggregationType === "HOUR" && opt?.meta) {
                            const base = pageWindow.start
                              ? new Date(pageWindow.start)
                              : new Date();
                            const year = base.getFullYear();
                            const month = base.getMonth();
                            const day = base.getDate();
                            const hour = base.getHours();

                            const { startMin, endMin } = opt.meta;
                            newStart = new Date(
                              year,
                              month,
                              day,
                              hour,
                              startMin,
                              0,
                              0
                            );
                            newEnd = new Date(
                              year,
                              month,
                              day,
                              hour,
                              startMin,
                              59,
                              999
                            );
                            nextAgg = "RAW";
                          }

                          if (newStart && newEnd && nextAgg) {
                            setAggregationType(nextAgg);
                            await fetchHistorical({
                              assetUuid,
                              aggType: nextAgg,
                              start: newStart,
                              end: newEnd,
                            });
                          }
                        }}
                        style={{ width: 360 }}
                        placeholder={
                          aggregationType === "YEAR"
                            ? "Select month"
                            : aggregationType === "MONTH"
                            ? "Select date"
                            : aggregationType === "DAY"
                            ? "Select hour"
                            : aggregationType === "HOUR"
                            ? "Select 15-min range"
                            : "Select"
                        }
                      >
                        {selectorOptions.map((o) => (
                          <Option
                            key={String(o.value)}
                            value={o.value}
                            disabled={o.disabled}
                          >
                            {o.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>

                {
                  (() => {
                    const conditionLines = (tags.find((t) => t.key === selectedKey)?.conditions || []).map((c) => ({
                      name: `Condition ${c.operator} ${c.value}`,
                      data: liveSeries.map((d) => ({ x: d.x, y: Number(c.value) })),
                    }));
                    const hasTarget = targetValue != null && liveSeries.length > 0;
                    const targetLine = hasTarget ? [{
                      name: "Target / Setpoint",
                      data: liveSeries.map(d => ({ x: d.x, y: targetValue })),
                    }] : [];
                    const liveSer = aggregationType === "LIVE"
                      ? [{ name: `${selectedKey} (Live)`, data: liveSeries }, ...conditionLines, ...targetLine]
                      : transformChartData(chartData, selectedKey, tags.find((t) => t.key === selectedKey), aggregationType);
                    // Build per-series stroke widths and dashArrays
                    const numSeries = liveSer.length;
                    const strokeWidths = liveSer.map((_, i) => (i === 0 ? 2 : 1.5));
                    const dashArrays = liveSer.map((s) => {
                      if (s.name === "Target / Setpoint") return 6;
                      if (s.name?.startsWith("Condition")) return 4;
                      return 0;
                    });
                    const seriesColors = liveSer.map((s) => {
                      if (s.name === "Target / Setpoint") return "#fa8c16";
                      if (s.name?.startsWith("Condition")) return "#ff4757";
                      return "#008FFB";
                    });
                    return (
                      <ReactApexChart
                        options={{
                          ...getChartOptions(aggregationType),
                          type: "line",
                          tooltip: { shared: true, intersect: false },
                          stroke: { curve: "smooth", width: strokeWidths, dashArray: dashArrays },
                          colors: aggregationType === "LIVE" ? seriesColors : undefined,
                          legend: { show: true },
                        }}
                        series={liveSer}
                        type="line"
                        height={320}
                      />
                    );
                  })()
                }

                {aggregationType !== "LIVE" && (
                  <div style={{ marginTop: 10, textAlign: "center" }}>
                    <Button onClick={onPrevious} style={{ marginRight: 8 }}>
                      Previous
                    </Button>
                    <Button onClick={onNext}>Next</Button>
                  </div>
                )}
              </>
            ) : (
              <p>Select a parameter from the left to view its graph.</p>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};



export default WriteControlScreen;